import { useEffect, useRef, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import { Calculation, UpdateCalculationBody } from "../types/calculations";
import { useCalculationsApi } from "../useCalculations";
import { BeamReinforcementConfiguration } from "./types/configuration";

export type BeamReinforcementConfigurationFormValues = BeamReinforcementConfiguration;

const keepDefined = <T>(value: T | undefined, fallback: T): T => (value === undefined ? fallback : value);

export function buildBeamReinforcementConfigurationDefaults(
  calculation?: Calculation<BeamReinforcementConfiguration>,
): BeamReinforcementConfigurationFormValues {
  const d = calculation?.data;

  return {
    calculationId: d?.calculationId ?? calculation?.id ?? "",
    unitSystem: d?.unitSystem ?? null,
    serviceClass: d?.serviceClass ?? 1,
    loadDuration: d?.loadDuration ?? "short",
    tipTolerance: d?.tipTolerance ?? 10,
    existingMaterial: d?.existingMaterial ?? null,
    existingBase: d?.existingBase ?? 120,
    existingHeight: d?.existingHeight ?? 240,
    existingLength: d?.existingLength ?? null,
    reinforcementMaterial: d?.reinforcementMaterial ?? null,
    reinforcementBase: d?.reinforcementBase ?? 80,
    reinforcementHeight: d?.reinforcementHeight ?? 240,
    reinforcementLength: d?.reinforcementLength ?? null,
    reinforcementSides: d?.reinforcementSides ?? 1,
    permanentLoad: d?.permanentLoad ?? 1.5,
    liveLoad: d?.liveLoad ?? 2,
    beamLength: d?.beamLength ?? 3,
    screw: d?.screw ?? null,
    screwSpacing: d?.screwSpacing ?? 200,
    preDrilled: d?.preDrilled ?? false,
  };
}

const mergeBeamReinforcementConfigurationValues = (
  values: Partial<BeamReinforcementConfigurationFormValues> | undefined,
  fallback: BeamReinforcementConfigurationFormValues,
): BeamReinforcementConfigurationFormValues => ({
  calculationId: keepDefined(values?.calculationId, fallback.calculationId),
  unitSystem: keepDefined(values?.unitSystem, fallback.unitSystem),
  serviceClass: keepDefined(values?.serviceClass, fallback.serviceClass),
  loadDuration: keepDefined(values?.loadDuration, fallback.loadDuration),
  tipTolerance: keepDefined(values?.tipTolerance, fallback.tipTolerance),
  existingMaterial: keepDefined(values?.existingMaterial, fallback.existingMaterial),
  existingBase: keepDefined(values?.existingBase, fallback.existingBase),
  existingHeight: keepDefined(values?.existingHeight, fallback.existingHeight),
  existingLength: keepDefined(values?.existingLength, fallback.existingLength),
  reinforcementMaterial: keepDefined(values?.reinforcementMaterial, fallback.reinforcementMaterial),
  reinforcementBase: keepDefined(values?.reinforcementBase, fallback.reinforcementBase),
  reinforcementHeight: keepDefined(values?.reinforcementHeight, fallback.reinforcementHeight),
  reinforcementLength: keepDefined(values?.reinforcementLength, fallback.reinforcementLength),
  reinforcementSides: keepDefined(values?.reinforcementSides, fallback.reinforcementSides),
  permanentLoad: keepDefined(values?.permanentLoad, fallback.permanentLoad),
  liveLoad: keepDefined(values?.liveLoad, fallback.liveLoad),
  beamLength: keepDefined(values?.beamLength, fallback.beamLength),
  screw: keepDefined(values?.screw, fallback.screw),
  screwSpacing: keepDefined(values?.screwSpacing, fallback.screwSpacing),
  preDrilled: keepDefined(values?.preDrilled, fallback.preDrilled),
});

const serializeForComparison = (v: BeamReinforcementConfigurationFormValues): string =>
  JSON.stringify({
    calculationId: v.calculationId,
    unitSystem: v.unitSystem,
    serviceClass: v.serviceClass,
    loadDuration: v.loadDuration,
    tipTolerance: v.tipTolerance,
    existingMaterial: v.existingMaterial,
    existingBase: v.existingBase,
    existingHeight: v.existingHeight,
    existingLength: v.existingLength,
    reinforcementMaterial: v.reinforcementMaterial,
    reinforcementBase: v.reinforcementBase,
    reinforcementHeight: v.reinforcementHeight,
    reinforcementLength: v.reinforcementLength,
    reinforcementSides: v.reinforcementSides,
    permanentLoad: v.permanentLoad,
    liveLoad: v.liveLoad,
    beamLength: v.beamLength,
    screw: v.screw,
    screwSpacing: v.screwSpacing,
    preDrilled: v.preDrilled,
  });

export type AutoSaveBeamReinforcementConfigurationReturn = {
  beamReinforcementConfiguration: BeamReinforcementConfiguration | null;
  isSaving: boolean;
};

export function useAutoSaveBeamReinforcementConfiguration(
  calculation: Calculation<BeamReinforcementConfiguration> | undefined,
  control: Control<BeamReinforcementConfigurationFormValues>,
  onError?: (error: unknown) => void,
): AutoSaveBeamReinforcementConfigurationReturn {
  const watchedValues = useWatch({ control });
  const { updateCalculation } = useCalculationsApi();
  const [persistedCalculation, setPersistedCalculation] = useState(calculation);
  const [isPersisting, setIsPersisting] = useState(false);
  const onErrorRef = useRef(onError);
  const lastSerializedRef = useRef(
    calculation ? serializeForComparison(buildBeamReinforcementConfigurationDefaults(calculation)) : "",
  );

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!calculation) return;
    setPersistedCalculation(calculation);
    lastSerializedRef.current = serializeForComparison(buildBeamReinforcementConfigurationDefaults(calculation));
  }, [calculation]);

  useEffect(() => {
    if (!calculation || !calculation.canEdit) return;

    const fallbackValues = buildBeamReinforcementConfigurationDefaults(calculation);
    const nextConfiguration = mergeBeamReinforcementConfigurationValues(
      watchedValues as Partial<BeamReinforcementConfigurationFormValues>,
      fallbackValues,
    );
    const nextSerialized = serializeForComparison(nextConfiguration);

    if (nextSerialized === lastSerializedRef.current) return;

    const timer = window.setTimeout(() => {
      lastSerializedRef.current = nextSerialized;
      setIsPersisting(true);

      const body: UpdateCalculationBody<BeamReinforcementConfiguration> = {
        name: calculation.name,
        description: calculation.description ?? null,
        data: nextConfiguration,
      };

      void updateCalculation(calculation.project?.id, calculation.id, body, {
        ...calculation,
        data: nextConfiguration,
      })
        .then((updated) => setPersistedCalculation(updated))
        .catch((err) => onErrorRef.current?.(err))
        .finally(() => setIsPersisting(false));
    }, 350);

    return () => window.clearTimeout(timer);
  }, [calculation, watchedValues, updateCalculation]);

  return {
    beamReinforcementConfiguration: persistedCalculation?.data ?? null,
    isSaving: isPersisting,
  };
}
